import { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import DescriptionIcon from "@mui/icons-material/Description";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { handleErrorMessage } from "helpers/methods";
import { RequestFile, RequestParticipant } from "models/interfaces";
import { requestParticipantsMenu } from "./ViewRequestHelper";
import {
  getRecipientInitials,
  getSecurityLabel,
  getSignerStatusVisual,
  recipientAuditKey,
} from "./ViewRequestStatusMeta";
import { getColor } from "../MySignRequestHelper";
import strings from "global/constants/StringConstants";
import { getDeclinedSignerDocumentPresignedUrl } from "../MySignRequestService";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { getDisplaySignerEmail } from "screens/InPersonSigning/InPersonSigningHelper";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens from "./ViewSignRequest.tokens";
import { tourAttrIf } from "tour";
import {
  CheckCircleGlyph,
  ChevronDownGlyph,
  KebabGlyph,
  LockGlyph,
  MessageGlyph,
  SigningOrderGlyph,
} from "./ViewRequestIcons";
import { getSignerLabel } from "../SignRequestsRowHelper";
import IdentityVerificationBadge from "./IdentityVerificationBadge";
import { SignerIdvSummary } from "types/identityVerification";
import { fillCopy } from "helpers/methods";

const styles = viewSignRequestStyles;
const { recipientRow } = viewSignRequestTokens;

interface Level {
  key: string;
  /** Chip label ("1", "CC") — null hides the level strip entirely. */
  num: string | null;
  label: string | null;
  parallel: boolean;
  recipients: RequestParticipant[];
}

interface RequestParticipantsProps {
  type: string;
  participants: RequestParticipant[];
  needOrder: boolean;
  anchorEl: any;
  handleMenuClose: (event: React.MouseEvent<HTMLElement>) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleIndividualAudit: (email: string) => void;
  selectedParticipant: RequestParticipant | undefined;
  setSelectedParticipant: (participant: RequestParticipant | undefined) => void;
  setShowFullAuditBtn: (value: boolean) => void;
  reqId: string;
  setLoader: (value: boolean) => void;
  status: string;
  signRequest: any;
  fetchData?: () => void;
  /** "Signed Jul 10, 2026 · 2:41 AM" per recipient, derived from the audit. */
  timestamps: Map<string, string>;
  /** Decline reasons per recipient, shown inline under the signer. */
  declineReasons: Map<string, string>;
  /**
   * Signers only, when the envelope requires identity verification: each
   * signer's summary from `getRequestIdentityVerification`, matched by id.
   */
  identityVerification?: SignerIdvSummary[];
}

const RequestParticipants = ({
  type,
  participants,
  needOrder,
  anchorEl,
  handleMenuClose,
  isMenuOpen,
  setIsMenuOpen,
  handleIndividualAudit,
  selectedParticipant,
  setSelectedParticipant,
  setShowFullAuditBtn,
  reqId,
  setLoader,
  status,
  signRequest,
  fetchData,
  timestamps,
  declineReasons,
  identityVerification,
}: RequestParticipantsProps) => {
  const isReviewers = type === strings.requestParticipantsType.reviewers;

  const manualReminderState = useFeatureState(FeatureKeys.REMINDER_MANUAL);
  const isManualReminderAvailable =
    manualReminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const manualReminderTooltip =
    manualReminderState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((manualReminderState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : manualReminderState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const [expanded, setExpanded] = useState(true);
  const [declinedDocumentsAnchor, setDeclinedDocumentsAnchor] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [selectedDocument, setSelectedDocument] = useState<RequestFile | null>(
    null,
  );
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDeclinedArrowClick = (
    event: React.MouseEvent<HTMLElement>,
    participant: RequestParticipant,
  ) => {
    event.stopPropagation();
    setDeclinedDocumentsAnchor((prev) => ({
      ...prev,
      [participant.email]: event.currentTarget,
    }));
  };

  const handleCloseDeclinedMenu = (email: string) => {
    setDeclinedDocumentsAnchor((prev) => ({ ...prev, [email]: null }));
  };

  const handleDocumentClick = async (
    documentFile: RequestFile,
    participantEmail: string,
  ) => {
    handleCloseDeclinedMenu(participantEmail);
    try {
      setPdfLoading(true);
      setPdfModalOpen(true);
      const presignedUrlDocument = await getDeclinedSignerDocumentPresignedUrl(
        signRequest?.id,
        documentFile,
      );
      setSelectedDocument(presignedUrlDocument);
    } catch (error: any) {
      handleErrorMessage(error);
      setPdfModalOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
    setSelectedDocument(null);
  };

  const isDeclined = (participantStatus: string | undefined) =>
    participantStatus?.toLowerCase() === strings.declined.toLowerCase();

  /** Matched by signer id; email is the fallback for a summary without one. */
  const idvSummaryFor = (participant: RequestParticipant) =>
    participant.cc
      ? undefined
      : identityVerification?.find(
          (summary) =>
            summary.signerId === participant.id ||
            (!!summary.email &&
              summary.email.toLowerCase() ===
                (participant.email ?? "").toLowerCase()),
        );

  const isParticipantVerificationLocked = (participant: any): boolean => {
    const attempts = participant?.requestSecurity?.failedAttempts;
    return (
      typeof attempts === "number" &&
      attempts >= strings.verificationMaxAttempts
    );
  };

  // ---- Group participants into levels ----
  const actionable = participants.filter((p) => !p.cc);
  const ccList = participants.filter((p) => p.cc);

  const buildLevels = (): Level[] => {
    const levels: Level[] = [];

    if (isReviewers || !needOrder) {
      levels.push({
        key: "all",
        num: null,
        label: isReviewers ? null : "ALL SIGNERS · SIGN IN ANY ORDER",
        parallel: false,
        recipients: actionable,
      });
    } else {
      const map = new Map<number, RequestParticipant[]>();
      actionable.forEach((p) => {
        const key = p.order ?? 0;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(p);
      });
      Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .forEach(([, recipients], index) => {
          levels.push({
            key: `level-${index}`,
            num: String(index + 1),
            label: `LEVEL ${index + 1}`,
            parallel: recipients.length > 1,
            recipients,
          });
        });
    }

    if (!isReviewers && ccList.length > 0) {
      levels.push({
        key: "cc",
        num: "CC",
        label: "RECEIVES FINAL COPY",
        parallel: false,
        recipients: ccList,
      });
    }

    return levels;
  };

  const levels = buildLevels();
  const hasLevelHeaders = levels.some((level) => level.label);
  const levelCount = levels.filter((level) => level.num !== "CC").length;

  // A participant is matched to their audit line by role + email, so a person
  // who is both an approver and a signer doesn't share one line across rows.
  const roleToken = isReviewers ? "reviewer" : "signer";

  const renderRecipient = (participant: RequestParticipant) => {
    /*
      An in-person signer has no name until the host captures it at handover,
      so the label falls back to the email and then to the delivery mode.
      Initials stay keyed to a real identity, leaving the person glyph for
      someone nobody has named yet.
    */
    const label = getSignerLabel(participant);
    const identity =
      (participant.name ?? "").trim() || (participant.email ?? "").trim();
    const { bg } = getColor(identity || "A");
    const pill = getSignerStatusVisual(participant.status);
    const auditKey = recipientAuditKey(roleToken, participant.email);
    const timestamp = participant.cc
      ? "Notified when completed"
      : timestamps.get(auditKey);
    const declineReason = isDeclined(participant.status)
      ? declineReasons.get(auditKey)
      : undefined;
    const canSeeHighlights =
      isDeclined(participant.status) && status === strings.declined;
    const idvSummary = idvSummaryFor(participant);

    return (
      <Box
        key={`${participant.email}-${participant.id}`}
        sx={styles.recipientRow(hasLevelHeaders)}
      >
        <Avatar sx={styles.recipientAvatar(bg)}>
          {getRecipientInitials(identity)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={styles.recipientNameRow}>
            <Tooltip title={label} arrow describeChild>
              <Typography component="span" sx={styles.recipientName}>
                {label}
              </Typography>
            </Tooltip>
            {participant.cc && (
              <Box component="span" sx={styles.recipientRole}>
                {strings.cc.toUpperCase()}
              </Box>
            )}
          </Box>

          <Typography component="div" sx={styles.recipientEmail}>
            {getDisplaySignerEmail(participant.email)}
          </Typography>

          <Box sx={styles.recipientMetaRow}>
            <Box component="span" sx={styles.recipientMetaItem}>
              <LockGlyph sx={styles.recipientMetaIcon} />
              {getSecurityLabel(participant)}
            </Box>
            {timestamp && <Box component="span">{timestamp}</Box>}
            {isParticipantVerificationLocked(participant) && (
              <Tooltip
                title={`This signer entered an incorrect verification code ${strings.verificationMaxAttempts} times. The signing link is locked — they cannot access the request until you resend it.`}
                arrow
                describeChild
              >
                <Box component="span" sx={styles.recipientMetaItem}>
                  <LockOutlinedIcon sx={styles.recipientMetaIcon} />
                  Locked
                </Box>
              </Tooltip>
            )}
          </Box>

          {idvSummary?.status === "BLOCKED" && (
            // Points at the existing Update / Void actions in the header menu;
            // no new action is built for this.
            <Box sx={styles.recipientNote} role="note">
              <LockOutlinedIcon sx={styles.recipientNoteIcon} />
              <Typography component="div" sx={styles.recipientNoteText}>
                {fillCopy(strings.identityVerification.blockedNote, {
                  name: label,
                  max: idvSummary.maxAttempts,
                })}
              </Typography>
            </Box>
          )}

          {declineReason && (
            <Box sx={styles.recipientNote}>
              <MessageGlyph sx={styles.recipientNoteIcon} />
              <Typography component="div" sx={styles.recipientNoteText}>
                <Box component="span" sx={{ fontWeight: 800 }}>
                  Decline reason:{" "}
                </Box>
                {declineReason}
              </Typography>
            </Box>
          )}
        </Box>

        {canSeeHighlights && (
          <Button
            size="small"
            onClick={(event) => handleDeclinedArrowClick(event, participant)}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  transform: declinedDocumentsAnchor[participant.email]
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            }
            sx={{
              color: recipientRow.noteIconColor,
              fontWeight: 700,
              fontSize: recipientRow.roleFontSize,
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            See Highlights
          </Button>
        )}

        <IdentityVerificationBadge summary={idvSummary} />

        {!participant.cc && participant.status && (
          <Box sx={styles.statusChip(pill.key, true)}>
            <Box component="span" sx={styles.statusDot(pill.key, true)} />
            <Box component="span" sx={styles.statusLabel(pill.key, true)}>
              {pill.label}
            </Box>
          </Box>
        )}

        <IconButton
          disableRipple
          aria-label={`${label} options`}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setIsMenuOpen(event);
            setSelectedParticipant(participant);
          }}
          sx={styles.recipientKebab}
        >
          <KebabGlyph
            sx={{
              width: recipientRow.kebabIconSize,
              height: recipientRow.kebabIconSize,
            }}
          />
        </IconButton>
      </Box>
    );
  };

  const orderLabel = isReviewers
    ? "APPROVES BEFORE SIGNING STARTS"
    : needOrder
      ? `SIGNS IN ORDER · ${levelCount} LEVEL${levelCount > 1 ? "S" : ""}`
      : "NO SIGNING ORDER";

  const orderBadge = (
    <Box sx={styles.sectionBadge}>
      {isReviewers ? (
        <CheckCircleGlyph sx={styles.sectionBadgeIcon} />
      ) : (
        <SigningOrderGlyph sx={styles.sectionBadgeIcon} />
      )}
      {orderLabel}
    </Box>
  );

  return (
    <Box {...tourAttrIf(!isReviewers, "viewRequest.signers")} sx={styles.card}>
      {/*
        Below `md` the Signers header drops its rule so the order caption can
        sit flush under it; Approvers keep theirs, as the reference draws it.
      */}
      <Box sx={styles.sectionHeader(isReviewers)}>
        <Box sx={styles.sectionHeaderLead}>
          <Box component="h2" sx={styles.sectionTitle}>
            {isReviewers ? "Approvers" : "Signers"}
          </Box>
          <Box component="span" sx={styles.sectionCount(isReviewers)}>
            {participants.length}
          </Box>
        </Box>
        <Box sx={styles.sectionHeaderActions}>
          {orderBadge}
          {!isReviewers && (
            <IconButton
              disableRipple
              onClick={() => setExpanded((value) => !value)}
              aria-label={expanded ? "Collapse signers" : "Expand signers"}
              sx={styles.sectionToggle(expanded)}
            >
              <ChevronDownGlyph sx={styles.sectionToggleGlyph} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* The compact reference re-lays the same caption as its own row. */}
      {!isReviewers && <Box sx={styles.sectionBadgeRow}>{orderLabel}</Box>}

      <Collapse in={isReviewers || expanded} timeout="auto" unmountOnExit>
        {levels.map((level) => (
          <Box key={level.key}>
            {level.label && (
              <Box sx={styles.levelHeader}>
                {level.num && (
                  <Box
                    component="span"
                    sx={styles.levelNum(level.num === "CC")}
                  >
                    {level.num}
                  </Box>
                )}
                <Box component="span" sx={styles.levelLabel}>
                  {level.label}
                </Box>
                {level.parallel && (
                  <Box component="span" sx={styles.levelParallel}>
                    SIGNS IN ANY ORDER
                  </Box>
                )}
                <Box sx={styles.levelRule} />
              </Box>
            )}
            {level.recipients.map(renderRecipient)}
          </Box>
        ))}
      </Collapse>

      {requestParticipantsMenu(
        anchorEl,
        isMenuOpen,
        handleMenuClose,
        handleIndividualAudit,
        selectedParticipant,
        setSelectedParticipant,
        setShowFullAuditBtn,
        reqId,
        setLoader,
        status,
        isManualReminderAvailable,
        manualReminderTooltip,
        fetchData,
      )}

      {participants.map((participant) => {
        if (!isDeclined(participant.status) || status !== strings.declined) {
          return null;
        }
        const email = participant.email;
        const anchor = declinedDocumentsAnchor[email];

        return (
          <Menu
            key={email}
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => handleCloseDeclinedMenu(email)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { maxHeight: 300, minWidth: 250, mt: 0.5 } }}
          >
            {signRequest?.files?.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No documents found
                </Typography>
              </MenuItem>
            ) : (
              signRequest?.files?.map((file: RequestFile, idx: number) => (
                <MenuItem
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDocumentClick(file, email);
                  }}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <DescriptionIcon
                    sx={{ fontSize: 18, mr: 1.5, color: "text.secondary" }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {file.unsignedPdf || file.unsignedDoc}
                      </Typography>
                    }
                  />
                </MenuItem>
              ))
            )}
          </Menu>
        );
      })}

      <AppModal
        open={pdfModalOpen}
        onClose={handleClosePdfModal}
        title={selectedDocument?.unsignedDoc || selectedDocument?.unsignedPdf}
        tone="neutral"
        // A PDF viewer — the widest step, and it runs its own scroller.
        size="xl"
        disableBodyPadding
        scrollBody={false}
        bodySx={{
          display: "flex",
          flexDirection: "column",
          height: "70vh",
          overflow: "hidden",
        }}
      >
        <>
          {pdfLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading PDF...</Typography>
            </Box>
          ) : selectedDocument?.unsignedDocUrl ||
            selectedDocument?.unsignedPdfUrl ? (
            <iframe
              src={`${selectedDocument.unsignedDocUrl || selectedDocument.unsignedPdfUrl}#toolbar=0`}
              style={{ width: "100%", height: "100%", border: "none", flex: 1 }}
              title={
                selectedDocument.unsignedDoc || selectedDocument.unsignedPdf
              }
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <Typography color="text.secondary">
                PDF URL not available
              </Typography>
            </Box>
          )}
        </>
      </AppModal>
    </Box>
  );
};

export default RequestParticipants;
